import React, { useEffect, useState } from "react";
import { notifySuccess, notifyError } from "../../Services/Notifier";
import Util from "../../Classes/Util";
import { CategoryService } from "./Services/CategoryService";
import { Variables } from "../../Variations/Variations";
import CategoryGallery from "./CategoryGallery";

const initialForm = {
  CategoryName: "",
  ArCategoryName: "",
  ParentId: 0,
  CategoryOrder: 0,
  Image: 0,
  IsPublished: false, // ✅ unchanged
  InMenu: false,      // ✅ NEW
};

const CategoryModal = ({ show, onClose, categoryId, onSaved }) => {
  const isNew = categoryId === null;

  const [form, setForm] = useState(initialForm);
  const [parents, setParents] = useState([]);
  const [showingMedia, setShowingMedia] = useState("");
  const [saving, setSaving] = useState(false);

  const update = (key, value) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const loadData = async () => {
    try {
      setParents(await CategoryService.getParents());

      if (!isNew) {
        const cat = await CategoryService.getById(categoryId);

        setForm({
          CategoryName: cat.CategoryName,
          ArCategoryName: cat.ArCategoryName ?? "",
          ParentId: cat.ParentId ?? 0,
          CategoryOrder: cat.CategoryOrder ?? 0,
          Image: cat.imageid ?? 0,
          IsPublished: cat.IsPublished ?? false, // ✅ untouched
          InMenu: cat.InMenu ?? false,           // ✅ NEW
        });

        setShowingMedia(cat.image ?? "");
      } else {
        setForm(initialForm);
        setShowingMedia("");
      }
    } catch {
      notifyError("Failed to load category data");
    }
  };

  const save = async () => {
    if (!form.CategoryName || !form.Image) {
      notifyError("Category name & image are required");
      return;
    }

    try {
      setSaving(true);

      if (isNew) {
        await CategoryService.create(form);
      } else {
        await CategoryService.update(categoryId, form);
      }

      notifySuccess(isNew ? "Category added" : "Category updated");
      onSaved();
      onClose();
    } catch (ex) {
      notifyError(typeof ex === "string" ? ex : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (show) loadData();
  }, [show]);

  if (!show) return null;

  return (
    <div className="modal fade show d-block" style={{ background: "#0006" }}>
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">

          {/* HEADER */}
          <div className="modal-header">
            <h5>{isNew ? "Add Category" : "Edit Category"}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>

          {/* BODY */}
          <div className="modal-body">

            {/* NAMES */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label>Name (EN)</label>
                <input
                  className="form-control"
                  value={form.CategoryName}
                  onChange={e => update("CategoryName", e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label>Name (AR)</label>
                <input
                  className="form-control text-end"
                  dir="rtl"
                  value={form.ArCategoryName}
                  onChange={e => update("ArCategoryName", e.target.value)}
                />
              </div>
            </div>

            {/* PARENT + ORDER */}
            <div className="row mb-3">
              <div className="col-md-6">
                <label>Parent</label>
                <select
                  className="form-control"
                  value={form.ParentId}
                  onChange={e => update("ParentId", +e.target.value)}
                >
                  <option value={0}>Root</option>
                  {parents.map(p => (
                    <option key={p.CategoryId} value={p.CategoryId}>
                      {p.CategoryName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <label>Order</label>
                <input
                  type="number"
                  className="form-control"
                  value={form.CategoryOrder}
                  onChange={e => update("CategoryOrder", +e.target.value)}
                />
              </div>
            </div>

            {/* ✅ IN MENU (NEW, INDEPENDENT) */}
            <div className="row mb-3">
              <div className="col-md-6">
                <div className="form-check mt-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="inMenuCheck"
                    checked={form.InMenu}
                    onChange={e =>
                      update("InMenu", e.target.checked)
                    }
                  />
                  <label className="form-check-label" htmlFor="inMenuCheck">
                    In Menu
                  </label>
                </div>
              </div>
            </div>

            {/* IMAGE */}
            <div className="mt-4">
              <CategoryGallery
                setMedia={m => update("Image", m)}
                setShowingMedia={setShowingMedia}
              />

              {showingMedia && (
                <img
                  src={Variables.API_PUBLISHED + showingMedia}
                  className="img-thumbnail mt-3"
                  width="250"
                  alt=""
                />
              )}
            </div>

          </div>

          {/* FOOTER */}
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              disabled={saving}
              onClick={save}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CategoryModal;
